import React, { useState, useEffect, useRef } from "react";
import SkeletonLine from "../Common/SkeletonLine";
import {
  calculateProfitMargin,
  calculateROI,
  calculateMaximumCost,
  calculateBreakevenSalePrice,
} from "../../utils/profitCalculator";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { formatNumber } from "../../utils/helper";
import { getCurrency } from "../../utils/helper";
import { getAlertData } from "../../utils/helper";
import TooltipSimple from "../Common/TooltipSimple";

const BasicInfo = ({
  data,
  loading,
  salesData,
  settingsData,
  referralFeePercent,
  alerts,
  asin,
  basicInfo,
}) => {
  const dispatch = useDispatch();
  const totalFees = useSelector((state) => state.globalStates.total_fees);
  const prices = useSelector((state) => state.globalStates.prices);
  const runAnalysis = useSelector((state) => state.globalStates.runAnalysis);
  const settings = settingsData;
  const selectedFulfillment = useSelector(
    (state) => state.globalStates.selectedFulfillment
  );

  const [profitDollars, setProfitDollars] = useState("");
  const [profitPercent, setProfitPercent] = useState("");
  const [profit, setProfit] = useState(data?.profit || "");
  const [roi, setROI] = useState(data?.roi || "");
  const [maxCost, setMaxCost] = useState(data?.max_cost || "");
  const [breakeven, setBreakeven] = useState(data?.brekeven || "");
  const [alertData, setAlertData] = useState([]);
  const [showAlerts, setShowAlerts] = useState(true);
  const [lpAlert, setLPAlert] = useState(false);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isInitialLoad.current) {
        return;
      }
      if (asin !== "-") {
        let cost_prices_object = JSON.parse(
          localStorage.getItem("cost_prices")
        );
        if (cost_prices_object) {
          cost_prices_object[asin] = prices.cost_price;
        } else {
          cost_prices_object = {
            [asin]: prices.cost_price,
          };
        }
        localStorage.setItem("cost_prices", JSON.stringify(cost_prices_object));
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // setTimeout to disable initial load flag
    setTimeout(() => {
      isInitialLoad.current = false; // <-- .current for refs
    }, 0);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [asin, prices.cost_price]);

  useEffect(() => {
    const { alertData, hasVisibleAlerts } = getAlertData(alerts, basicInfo);
    setAlertData(alertData);
    setShowAlerts(hasVisibleAlerts);
  }, [alerts, basicInfo]);

  useEffect(() => {
    if (data) {
      const salePrice = parseFloat(prices.sale_price) || 0;
      const costPrice = parseFloat(prices.cost_price) || 0;
      // get the cost price from localstorage
      const cost_prices_object = JSON.parse(
        localStorage.getItem("cost_prices")
      );
      if (
        cost_prices_object &&
        cost_prices_object.hasOwnProperty(asin) &&
        asin !== "-"
      ) {
        dispatch({
          type: "SET_PRICES",
          payload: {
            sale_price: data.prices?.sale_price || "",
            cost_price:
              cost_prices_object[asin] || data.prices?.cost_price || "",
          },
        });
      } else {
        dispatch({
          type: "SET_PRICES",
          payload: {
            sale_price: data.prices?.sale_price || "",
            cost_price: data.prices?.cost_price || "",
          },
        });
      }
      const [profitDollars, profitPercent] = calculateProfitMargin(
        salePrice,
        cost_prices_object && asin !== "-" && cost_prices_object[asin]
          ? cost_prices_object[asin]
          : costPrice,
        totalFees
      );
      setProfitDollars(profitDollars || "");
      setProfitPercent(data.profit_percent || "");
      setProfit(data.profit || "");
      setROI(data.roi || "");
      setMaxCost(data.max_cost || "");
    }
  }, [data]);

  console.log("runAnalysis", runAnalysis);
  useEffect(() => {
    const salePrice = parseFloat(prices.sale_price) || 0;
    const costPrice = parseFloat(prices.cost_price) || 0;
  
    const [profitDollarsTemp, profitPercent] = calculateProfitMargin(
      salePrice,
      costPrice,
      totalFees
    );

    const roiValue = calculateROI(salePrice, costPrice, totalFees);

    // setmaxcost only ifsale price changes
    if (
      (prices.sale_price !== undefined &&
        data?.prices?.sale_price !== undefined &&
        prices.sale_price !== data?.prices?.sale_price) ||
      (settings?.defaultValues?.local_fullfillment &&
        settings?.defaultValues?.local_fullfillment !== selectedFulfillment)
    ) {
      const max_cost = calculateMaximumCost(
        salePrice,
        costPrice,
        totalFees,
        Number(settings?.buyingCriteria?.minProfit) || 0,
        profitDollarsTemp,
        settings
      );
      console.log("max_cost", max_cost);
      setMaxCost(max_cost);
    } else {
      setMaxCost(data?.max_cost);
    }
    // set breakeven only if cost price changes
    if (prices.cost_price !== data?.prices?.cost_price) {
      const breakevenSalePrice = calculateBreakevenSalePrice(
        costPrice,
        salePrice,
        totalFees,
        referralFeePercent
      );
      setBreakeven(breakevenSalePrice);
    } else {
      setBreakeven(data?.brekeven);
    }
    // if product is low price and if new profit with greater sale price is lower then show alert
    if (alerts?.low_price_FBA && salePrice >= 10) {
      if (
        profitDollarsTemp !== "--" &&
        Number(profitDollarsTemp) < Number(data.profit)
      ) {
        setLPAlert(true);
      }
    }
    // setMaxCost(`${max_cost}`);
    setProfitDollars(
      profitDollarsTemp && profitDollarsTemp !== "NaN"
        ? profitDollarsTemp
        : "--"
    );
    setProfitPercent(
      profitPercent && profitPercent !== "NaN" ? profitPercent : "--"
    );
    setProfit(`${getCurrency(settingsData?.marketplace)}${profitDollarsTemp}`);
    setROI(`${roiValue.toFixed(2)}`);
  }, [prices, totalFees]);

  const bsr = data?.BSR;
  const estimatedPrice = salesData?.sales_estimation?.monthly_sales;

  const skeletonBlock = (
    <div className="mb-2 px-2 w-full">
      <SkeletonLine />
    </div>
  );

  const metricBaseClass = "rounded-lg relative mr-2 flex flex-col flex-1";

  const metricsConfig = [
    {
      key: "profit",
      show: settingsData?.quickInfo?.show_profit === true,
      className: `${metricBaseClass} ${
        !runAnalysis ? "text-[#3a4240]" 
        : profitDollars < Number(settingsData?.buyingCriteria?.minProfit)
          ? "text-white"
          : "text-primary"
      }`,
      render: () => (
        <>
          <div className="text-[#a8a8a8] text-sm md:text-md">Profit</div>
          {loading ? (
            skeletonBlock
          ) : (
            <div className="relative group text-lg md:text-xl w-fit font-semibold">
              {!runAnalysis ? "Run Analysis" : profit ? profit : "--"}
            </div>
          )}
        </>
      ),
    },
    {
      key: "profitMargin",
      show: settingsData?.quickInfo?.show_profit_margin === true,
      className: `${metricBaseClass} ${
        !runAnalysis ? "text-[#3a4240]" 
        : profitDollars < Number(settingsData?.buyingCriteria?.minProfit)
          ? "text-[#E74B4B]"
          : "text-primary "
      }`,
      render: () => (
        <>
          <div className="text-[#a8a8a8] text-sm md:text-md">Profit Margin</div>
          {loading ? (
            skeletonBlock
          ) : (
            <div className="relative group text-lg md:text-xl w-fit font-semibold">
              {!runAnalysis ? "Run Analysis" : profitPercent ? profitPercent : "--"}
            </div>
          )}
        </>
      ),
    },
    {
      key: "roi",
      show: settingsData?.quickInfo?.show_roi === true,
      className: `${metricBaseClass} ${
        !runAnalysis ? "text-[#3a4240]" 
        : roi < Number(settingsData?.buyingCriteria?.minroi)
          ? "text-[#E74B4B]"
          : "text-primary"
      }`,
      render: () => (
        <>
          <div className="text-[#a8a8a8] text-sm md:text-md">ROI</div>
          {loading ? (
            skeletonBlock
          ) : (
            <div className="relative group text-lg md:text-xl w-fit font-semibold">
              {!runAnalysis ? "Run Analysis" : roi && roi !== "NaN" ? roi + "%" : "--"}
            </div>
          )}
        </>
      ),
    },
    {
      key: "breakeven",
      show: settingsData?.quickInfo?.show_breakeven === true,
      className: `${metricBaseClass} ${
        !runAnalysis ? "text-[#3a4240]" 
        : Number(prices?.sale_price) < Number(data?.brekeven)
          ? "text-[#E74B4B]"
          : "text-primary"
      }`,
      render: () => (
        <>
          <div className="text-[#a8a8a8] text-sm md:text-md">Break Even</div>
          {loading ? (
            skeletonBlock
          ) : (
            <div className="relative group text-lg md:text-xl w-fit font-semibold">
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                {!runAnalysis ? "Run Analysis" : breakeven
                  ? getCurrency(settingsData?.marketplace) + breakeven
                  : "--"}
              </span>
            </div>
          )}
        </>
      ),
    },
  ];

  const visibleMetrics = metricsConfig.filter((metric) => metric.show);
  const hiddenMetricsCount = metricsConfig.length - visibleMetrics.length;

  return (
    <div className="product-details-container rounded-lg text-white">
      <div className="flex justify-between gap-x-2">
        {showAlerts ? (
          <div className="w-full bg-secondary-bg mt-2 rounded-lg flex px-4 py-1.5 items-center justify-between">
            <div className="text-left text-xs">Alerts:</div>
            <div className="flex items-center gap-x-1">
              {loading
                ? [1, 2, 3].map((e) => {
                    return (
                      <div className="animate-pulse" key={e} role="status">
                        <div className="rounded-md bg-primary px-3 py-2"></div>
                      </div>
                    );
                  })
                : alertData.length > 0 &&
                  alertData.map(({ title, color, tooltipText, hidden }, i) => {
                    return (
                      <div
                        className={`
                        ${
                          title === "LP" && lpAlert
                            ? "bg-[#E74B4B]"
                            : `bg-${color}-600/90 border border-${color}-600 text-${color}-600`
                        } 
                        px-2 py-1 rounded-md group relative 
                        ${hidden ? "hidden" : ""}
                      `}
                        key={i}
                      >
                        <p className="font-semibold text-[10px]">{title}</p>
                        <TooltipSimple
                          text={
                            title === "LP" && lpAlert
                              ? "This product is more profitable at low price fee"
                              : tooltipText
                          }
                        />
                      </div>
                    );
                  })}
            </div>
          </div>
        ) : null}
      </div>
      <div className="flex justify-between mt-3 gap-x-2">
        <div
          className={`w-full rounded-lg flex py-2 gap-y-1 flex-col relative 
                ${
                  Number(settingsData?.buyingCriteria?.minbsr) === 0
                    ? "text-white"
                    : Number(settingsData?.buyingCriteria?.minbsr) >
                        Number(
                          data?.BSR?.percentage?.substring(
                            0,
                            data?.BSR?.percentage.length - 1
                          )
                        ) || bsr?.BSR === -1
                    ? "text-red-600"
                    : "text-white"
                }`}
        >
          <div className="text-[#a8a8a8] text-sm md:text-md">BSR</div>
          {loading ? (
            <div className="mb-2 px-2 w-full">
              <SkeletonLine />{" "}
            </div>
          ) : (
            <div className="relative group text-lg md:text-xl w-fit font-semibold">
              {bsr?.BSR !== -1 ? (
                <>
                  <span className="mr-1 overflow-hidden text-ellipsis whitespace-nowrap">
                    {bsr?.BSR ? formatNumber(Number(bsr?.BSR)) : "--"}
                  </span>
                  <span>
                    {data?.BSR?.percentage
                      ? "(" + data?.BSR?.percentage + ")"
                      : "(--)"}
                  </span>
                </>
              ) : (
                "--"
              )}
              <TooltipSimple
                text={
                  Number(settingsData?.buyingCriteria?.minbsr) >
                  Number(data?.BSR?.BSR)
                    ? `BSR(${data?.BSR?.BSR}) is less than desired BSR(${settingsData?.buyingCriteria?.minbsr})`
                    : `BSR(${data?.BSR?.BSR}) is above desired BSR level (${settingsData?.buyingCriteria?.minbsr})`
                }
              />
            </div>
          )}
        </div>
        <div className={`w-full rounded-lg flex flex-col py-2 gap-y-1 relative`}>
          <div className="text-[#a8a8a8] text-sm md:text-md">Est. Sales</div>
          {loading ? (
            <div className="mb-2 px-2 w-full">
              <SkeletonLine />{" "}
            </div>
          ) : (
            <div className="relative group text-lg md:text-xl font-semibold w-fit">
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                {estimatedPrice !== -1 && estimatedPrice
                  ? formatNumber(estimatedPrice) + "+/month"
                  : "--"}
              </span>
              <TooltipSimple
                text={
                  salesData?.sales_estimation?.message
                    ? salesData?.sales_estimation?.message
                    : estimatedPrice !== -1 && estimatedPrice
                    ? `We estimate this product sells ${formatNumber(
                        estimatedPrice
                      )} units per month`
                    : "Est. Sales: --"
                }
                width={"extra"}
              />
            </div>
          )}
        </div>
        <div className={`w-full rounded-lg flex flex-col py-2 gap-y-1 relative`}>
          <div className="text-[#a8a8a8] text-sm md:text-md">Max. Cost</div>
          {loading ? (
            <div className="mb-2 px-2 w-full">
              <SkeletonLine />{" "}
            </div>
          ) : (
            <div className="relative group text-lg md:text-xl font-semibold w-fit">
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                {maxCost && maxCost !== "NaN" && maxCost > 0
                  ? getCurrency(settingsData?.marketplace) + maxCost
                  : "--"}
              </span>
              <TooltipSimple
                text={`Maximum Cost: ${
                  maxCost && maxCost !== "NaN"
                    ? getCurrency(settingsData?.marketplace) + maxCost
                    : "--"
                }`}
              />
            </div>
          )}
        </div>
        <div className="w-full"></div>
      </div>

      <div className="flex justify-between mt-4 gap-3 md:gap-0">
        {visibleMetrics.map((metric) => (
          <div key={metric.key} className={metric.className}>
            {metric.render()}
          </div>
        ))}
        {Array.from({ length: hiddenMetricsCount }).map((_, index) => (
          <div
            key={`placeholder-${index}`}
            className={`${metricBaseClass} opacity-0 pointer-events-none select-none`}
          >
            <div className="text-[#a8a8a8] text-sm md:text-md">Placeholder</div>
            <div className="relative group text-lg md:text-xl w-fit font-semibold">
              --
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BasicInfo;
